import { jsx } from "react/jsx-runtime";
import { createContext, useMemo, useContext } from "react";
import { getViewTimePeriod } from "../helpers/getViewTimePeriod.js";
import { getEmptyCalendarSlots } from "../helpers/getEmptyCalendarSlots.js";
import { groupByEmployee, groupWorkingTimesByDate, groupNonWorkingTimesByDate, groupNonWorkingTimes } from "../helpers/groupBy.js";
import { fillEmptyDays } from "../helpers/fillEmptyDays.js";
import { useWorkingTimes } from "../hooks/useWorkingTimes.js";
import "date-fns";
import "../appointment-schedule.constants.js";
import "../appointment-schedule.types.js";
import "../../../../utils/time/time.js";
import "lodash";
import "../helpers/common.js";
import "date-fns/locale";
import "fast-memoize";
import "@tanstack/react-query";
import "./axios-provider.js";
import "axios";
const WorkingTimesContext = createContext({
  nonWorkingTimes: {},
  queryKey: null,
  workingTimes: {},
  queryResult: null
});
const WorkingTimesProvider = ({
  children,
  auth,
  shiftPlannerBaseURL,
  clinicReferenceId,
  view = "week",
  employees = [],
  date = /* @__PURE__ */ new Date()
}) => {
  const timePeriod = useMemo(() => getViewTimePeriod(view, date, true), [view, date]);
  const params = useMemo(
    () => ({
      ...timePeriod,
      employeeReferenceIds: employees.map(({ referenceId }) => referenceId)
    }),
    [date, employees, auth, shiftPlannerBaseURL, clinicReferenceId, view]
  );
  const { data, queryKey, queryResult } = useWorkingTimes({
    clinicReferenceId,
    auth,
    baseURL: shiftPlannerBaseURL,
    params
  });
  const workingTimesWithFilledEmptyDays = useMemo(
    () => fillEmptyDays(data, employees, timePeriod),
    [timePeriod, timePeriod, employees, data, auth]
  );
  const workingTimes = useMemo(
    () => groupByEmployee(groupWorkingTimesByDate(workingTimesWithFilledEmptyDays)),
    [workingTimesWithFilledEmptyDays]
  );
  const groupedNonWorkingTimes = useMemo(
    () => groupByEmployee(groupNonWorkingTimesByDate(workingTimesWithFilledEmptyDays ?? [])),
    [workingTimesWithFilledEmptyDays]
  );
  const calendarEmptySlots = useMemo(() => getEmptyCalendarSlots(workingTimes, groupedNonWorkingTimes), [workingTimes]);
  const nonWorkingTimes = useMemo(
    () => groupNonWorkingTimes(calendarEmptySlots, groupedNonWorkingTimes),
    [calendarEmptySlots, groupedNonWorkingTimes]
  );
  const value = useMemo(
    () => ({
      queryKey,
      nonWorkingTimes,
      workingTimes,
      queryResult
    }),
    [queryKey, data, nonWorkingTimes]
  );
  return /* @__PURE__ */ jsx(WorkingTimesContext.Provider, { value, children });
};
const useWorkingTimesProvider = () => {
  const context = useContext(WorkingTimesContext);
  if (context === void 0) {
    throw new Error("useWorkingTimesProvider must be used within a WorkingTimesProvider");
  }
  return context;
};
export {
  WorkingTimesProvider,
  useWorkingTimesProvider
};
